import React, { useEffect, useState, Component } from 'react';
import { motion } from 'framer-motion';
import { Plus, Calendar, DollarSign, Trash2, Loader2, AlertCircle, TrendingDown, Award, LayoutGrid, CalendarDays } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../utils/supabaseClient';
import { Abonnement, CreateAbonnementInput } from '../types/abonnement';
import { trackEvent } from '../utils/analytics';
import { formatXOF, calculateFees, PROVIDERS, getProviderFees } from '../utils/feeCalculator';
import { Header } from '../components/Header';
import { PaymentCalendar } from '../components/PaymentCalendar';
import { UpcomingPayments } from '../components/UpcomingPayments';
import { Provider, Currency } from '../types/calculator';
interface AbonnementWithSavings extends Abonnement {
  bestProvider: Provider;
  bestProviderCost: number;
  worstProviderCost: number;
  monthlySavings: number;
  yearlySavings: number;
  providerCosts: Record<Provider, number>;
}
type ViewMode = 'list' | 'calendar';
export function DashboardPage() {
  const {
    user
  } = useAuth();
  const [abonnements, setAbonnements] = useState<Abonnement[]>([]);
  const [abonnementsWithSavings, setAbonnementsWithSavings] = useState<AbonnementWithSavings[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [currencyFilter, setCurrencyFilter] = useState<'XOF' | 'EUR' | 'USD'>('USD');
  const [viewMode, setViewMode] = useState<ViewMode>('calendar');
  // Fetch abonnements
  useEffect(() => {
    if (user) {
      fetchAbonnements();
    }
  }, [user]);
  // Calculate savings when abonnements change
  useEffect(() => {
    if (abonnements.length > 0) {
      calculateSavingsForAll();
    }
  }, [abonnements]);
  const fetchAbonnements = async () => {
    if (!supabase || !user) return;
    try {
      setIsLoading(true);
      const {
        data,
        error
      } = await supabase.from('abonnements').select('*').eq('user_id', user.id).order('next_payment_date', {
        ascending: true
      });
      if (error) throw error;
      setAbonnements(data || []);
    } catch (err) {
      setError('Erreur lors du chargement des abonnements');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };
  const calculateSavingsForAll = () => {
    const withSavings: AbonnementWithSavings[] = abonnements.map(abonnement => {
      const currency = abonnement.currency as Currency;
      const amount = abonnement.amount;
      // Calculate cost for each provider
      const providerCosts: Record<string, number> = {};
      const providers = Object.keys(PROVIDERS) as Provider[];
      providers.forEach(provider => {
        const breakdown = calculateFees(amount, currency, provider);
        const fees = getProviderFees();
        const providerFee = fees[provider];
        const feeAmount = providerFee === 'not_defined' ? 0 : providerFee;
        providerCosts[provider] = breakdown.totalCost + feeAmount;
      });
      // Find best (cheapest) and worst (most expensive) providers
      const sortedProviders = providers.sort((a, b) => providerCosts[a] - providerCosts[b]);
      const bestProvider = sortedProviders[0];
      const worstProvider = sortedProviders[sortedProviders.length - 1];
      const bestProviderCost = providerCosts[bestProvider];
      const worstProviderCost = providerCosts[worstProvider];
      const monthlySavings = worstProviderCost - bestProviderCost;
      const yearlySavings = monthlySavings * 12;
      return {
        ...abonnement,
        bestProvider,
        bestProviderCost,
        worstProviderCost,
        monthlySavings,
        yearlySavings,
        providerCosts: providerCosts as Record<Provider, number>
      };
    });
    setAbonnementsWithSavings(withSavings);
  };
  const handleDelete = async (id: string) => {
    if (!supabase || !confirm('Êtes-vous sûr de vouloir supprimer cet abonnement ?')) return;
    try {
      const {
        error
      } = await supabase.from('abonnements').delete().eq('id', id);
      if (error) throw error;
      setAbonnements(abonnements.filter(a => a.id !== id));
      trackEvent('Abonnement Deleted', {
        abonnement_id: id
      });
    } catch (err) {
      alert('Erreur lors de la suppression');
      console.error(err);
    }
  };
  const calculateDaysUntil = (nextDate: string) => {
    const today = new Date();
    const next = new Date(nextDate);
    const diff = Math.ceil((next.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return diff;
  };
  const getTotalByOriginalCurrency = () => {
    return abonnements.reduce((sum, a) => {
      if (a.currency === currencyFilter) {
        return sum + a.amount;
      }
      return sum;
    }, 0);
  };
  const formatAmount = (amount: number, currency: Currency) => {
    if (currency === 'XOF') {
      return formatXOF(amount);
    } else if (currency === 'EUR') {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'EUR'
      }).format(amount);
    } else {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(amount);
    }
  };
  if (!user) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-id="element-413">
        <div className="text-center" data-id="element-414">
          <p className="text-gray-600 mb-4" data-id="element-415">
            Vous devez être connecté pour accéder au dashboard
          </p>
          <a href="/login" className="text-blue-600 hover:text-blue-700 font-medium" data-id="element-416">
            Se connecter
          </a>
        </div>
      </div>;
  }
  return <div className="min-h-screen bg-gray-50" data-id="element-417">
      <Header data-id="element-418" />

      {/* Page Header */}
      <div className="bg-white border-b border-gray-200" data-id="element-419">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-id="element-420">
          <div className="flex justify-between items-center" data-id="element-421">
            <div data-id="element-422">
              <h1 className="text-3xl font-bold text-gray-900" data-id="element-423">
                Mes Abonnements
              </h1>
              <p className="text-gray-600 mt-1" data-id="element-424">
                Gérez vos abonnements récurrents
              </p>
            </div>
            <div className="flex items-center gap-3" data-id="element-425">
              {/* View Mode Toggle */}
              <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1" data-id="element-426">
                <button onClick={() => setViewMode('calendar')} className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${viewMode === 'calendar' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-427">
                  <CalendarDays className="w-4 h-4" data-id="element-428" />
                  Calendrier
                </button>
                <button onClick={() => setViewMode('list')} className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${viewMode === 'list' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-429">
                  <LayoutGrid className="w-4 h-4" data-id="element-430" />
                  Liste
                </button>
              </div>

              <button onClick={() => setShowAddForm(true)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium" data-id="element-431">
                <Plus className="w-5 h-5" data-id="element-432" />
                Ajouter
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-433">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-id="element-434">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="bg-white rounded-xl shadow-sm p-6 border border-gray-200" data-id="element-435">
            <div className="flex items-center gap-3" data-id="element-436">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center" data-id="element-437">
                <Calendar className="w-6 h-6 text-blue-600" data-id="element-438" />
              </div>
              <div data-id="element-439">
                <p className="text-sm text-gray-600" data-id="element-440">Total abonnements</p>
                <p className="text-2xl font-bold text-gray-900" data-id="element-441">
                  {abonnements.length}
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="bg-white rounded-xl shadow-sm p-6 border border-gray-200" data-id="element-442">
            <div className="flex items-center gap-3" data-id="element-443">
              <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center" data-id="element-444">
                <DollarSign className="w-6 h-6 text-emerald-600" data-id="element-445" />
              </div>
              <div className="flex-1" data-id="element-446">
                <p className="text-sm text-gray-600 mb-2" data-id="element-447">
                  Montant total à venir
                </p>
                <p className="text-2xl font-bold text-gray-900" data-id="element-448">
                  {formatAmount(getTotalByOriginalCurrency(), currencyFilter)}
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="bg-white rounded-xl shadow-sm p-6 border border-gray-200" data-id="element-449">
            <div data-id="element-450">
              <p className="text-sm text-gray-600 mb-3" data-id="element-451">Filtrer par devise</p>
              <div className="flex gap-2" data-id="element-452">
                <button onClick={() => setCurrencyFilter('XOF')} className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${currencyFilter === 'XOF' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-453">
                  XOF
                </button>
                <button onClick={() => setCurrencyFilter('EUR')} className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${currencyFilter === 'EUR' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-454">
                  EUR
                </button>
                <button onClick={() => setCurrencyFilter('USD')} className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${currencyFilter === 'USD' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-455">
                  USD
                </button>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Upcoming Payments Section */}
        {!isLoading && !error && abonnements.length > 0 && <div className="mb-8" data-id="element-456">
            <UpcomingPayments abonnements={abonnements} data-id="element-457" />
          </div>}

        {/* Content based on view mode */}
        {isLoading ? <div className="flex justify-center items-center py-12" data-id="element-458">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" data-id="element-459" />
          </div> : error ? <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800" data-id="element-460">
            {error}
          </div> : abonnementsWithSavings.length === 0 ? <div className="bg-white rounded-xl shadow-sm p-12 text-center border border-gray-200" data-id="element-461">
            <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" data-id="element-462" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-463">
              Aucun abonnement
            </h3>
            <p className="text-gray-600 mb-6" data-id="element-464">
              Commencez à suivre vos abonnements récurrents
            </p>
            <button onClick={() => setShowAddForm(true)} className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium" data-id="element-465">
              <Plus className="w-5 h-5" data-id="element-466" />
              Ajouter votre premier abonnement
            </button>
          </div> : viewMode === 'calendar' ? <PaymentCalendar abonnements={abonnements} data-id="element-467" /> : <div className="space-y-4" data-id="element-468">
            {abonnementsWithSavings.map((abonnement, index) => {
          const daysUntil = calculateDaysUntil(abonnement.next_payment_date);
          const isUrgent = daysUntil <= 7;
          const isPast = daysUntil < 0;
          const bestProviderInfo = PROVIDERS[abonnement.bestProvider];
          return <motion.div key={abonnement.id} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: index * 0.05
          }} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-469">
                  <div className="p-6" data-id="element-470">
                    <div className="flex justify-between items-start mb-4" data-id="element-471">
                      <div className="flex-1" data-id="element-472">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-473">
                          {abonnement.name}
                        </h3>
                        <div className="flex flex-wrap gap-4 text-sm text-gray-600" data-id="element-474">
                          <div className="flex items-center gap-2" data-id="element-475">
                            <DollarSign className="w-4 h-4" data-id="element-476" />
                            <span className="font-medium" data-id="element-477">
                              {formatAmount(abonnement.amount, abonnement.currency as Currency)}
                            </span>
                          </div>
                          <div className="flex items-center gap-2" data-id="element-478">
                            <Calendar className="w-4 h-4" data-id="element-479" />
                            <span data-id="element-480">
                              Prochain:{' '}
                              {new Date(abonnement.next_payment_date).toLocaleDateString('fr-FR')}
                            </span>
                          </div>
                        </div>
                        <div className="mt-3" data-id="element-481">
                          {isPast ? <span className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium" data-id="element-482">
                              <AlertCircle className="w-4 h-4" data-id="element-483" />
                              En retard de {Math.abs(daysUntil)} jour
                              {Math.abs(daysUntil) > 1 ? 's' : ''}
                            </span> : isUrgent ? <span className="inline-flex items-center gap-1 px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-medium" data-id="element-484">
                              <AlertCircle className="w-4 h-4" data-id="element-485" />
                              Dans {daysUntil} jour{daysUntil > 1 ? 's' : ''}
                            </span> : <span className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm" data-id="element-486">
                              Dans {daysUntil} jours
                            </span>}
                        </div>
                      </div>
                      <button onClick={() => handleDelete(abonnement.id)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" data-id="element-487">
                        <Trash2 className="w-5 h-5" data-id="element-488" />
                      </button>
                    </div>

                    {/* Best Provider Section */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-489">
                      <div className="flex items-start justify-between gap-4" data-id="element-490">
                        <div className="flex-1" data-id="element-491">
                          <div className="flex items-center gap-2 mb-2" data-id="element-492">
                            <Award className="w-5 h-5 text-blue-600" data-id="element-493" />
                            <span className="font-semibold text-blue-900" data-id="element-494">
                              Meilleur choix: {bestProviderInfo.name}
                            </span>
                          </div>
                          <p className="text-sm text-blue-700" data-id="element-495">
                            Coût total avec {bestProviderInfo.name}:{' '}
                            <span className="font-bold" data-id="element-496">
                              {formatXOF(abonnement.bestProviderCost)}
                            </span>
                          </p>
                        </div>
                        <img src={bestProviderInfo.logoUrl} alt={bestProviderInfo.name} className="w-12 h-12 rounded-lg object-contain bg-white p-1" data-id="element-497" />
                      </div>
                    </div>

                    {/* Provider Comparison */}
                    <details className="mt-4" data-id="element-498">
                      <summary className="cursor-pointer text-sm text-gray-600 hover:text-gray-900 font-medium" data-id="element-499">
                        Voir la comparaison détaillée
                      </summary>
                      <div className="mt-3 space-y-2" data-id="element-500">
                        {(Object.keys(abonnement.providerCosts) as Provider[]).sort((a, b) => abonnement.providerCosts[a] - abonnement.providerCosts[b]).map(provider => {
                    const providerInfo = PROVIDERS[provider];
                    const cost = abonnement.providerCosts[provider];
                    const isBest = provider === abonnement.bestProvider;
                    return <div key={provider} className={`flex items-center justify-between p-3 rounded-lg border ${isBest ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'}`} data-id="element-501">
                                <div className="flex items-center gap-3" data-id="element-502">
                                  <img src={providerInfo.logoUrl} alt={providerInfo.name} className="w-8 h-8 rounded object-contain bg-white p-0.5" data-id="element-503" />
                                  <span className={`text-sm font-medium ${isBest ? 'text-blue-900' : 'text-gray-700'}`} data-id="element-504">
                                    {providerInfo.name}
                                  </span>
                                  {isBest && <span className="text-xs bg-blue-200 text-blue-800 px-2 py-0.5 rounded-full font-medium" data-id="element-505">
                                      MEILLEUR
                                    </span>}
                                </div>
                                <span className={`text-sm font-bold ${isBest ? 'text-blue-900' : 'text-gray-900'}`} data-id="element-506">
                                  {formatXOF(cost)}
                                </span>
                              </div>;
                  })}
                      </div>
                    </details>
                  </div>
                </motion.div>;
        })}
          </div>}
      </div>

      {/* Add Abonnement Modal */}
      {showAddForm && <AddAbonnementModal onClose={() => setShowAddForm(false)} onSuccess={() => {
      setShowAddForm(false);
      fetchAbonnements();
    }} data-id="element-507" />}
    </div>;
}
// Add Abonnement Modal Component (unchanged from before)
function AddAbonnementModal({
  onClose,
  onSuccess
}: {
  onClose: () => void;
  onSuccess: () => void;
}) {
  const {
    user
  } = useAuth();
  const [formData, setFormData] = useState<CreateAbonnementInput>({
    name: '',
    amount: 0,
    currency: 'XOF',
    last_payment_date: new Date().toISOString().split('T')[0]
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!supabase || !user) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const lastDate = new Date(formData.last_payment_date);
      const nextDate = new Date(lastDate);
      nextDate.setDate(nextDate.getDate() + 30);
      const {
        error: insertError
      } = await supabase.from('abonnements').insert({
        user_id: user.id,
        name: formData.name,
        amount: formData.amount,
        currency: formData.currency,
        last_payment_date: formData.last_payment_date,
        next_payment_date: nextDate.toISOString().split('T')[0]
      });
      if (insertError) throw insertError;
      trackEvent('Abonnement Created', {
        name: formData.name,
        amount: formData.amount,
        currency: formData.currency
      });
      onSuccess();
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la création');
    } finally {
      setIsSubmitting(false);
    }
  };
  return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-508">
      <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: 1,
      scale: 1
    }} className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6" data-id="element-509">
        <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-510">
          Nouvel abonnement
        </h2>

        {error && <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-red-800 text-sm" data-id="element-511">
            {error}
          </div>}

        <form onSubmit={handleSubmit} className="space-y-4" data-id="element-512">
          <div data-id="element-513">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-514">
              Nom du service
            </label>
            <input type="text" value={formData.name} onChange={e => setFormData({
            ...formData,
            name: e.target.value
          })} placeholder="Netflix, Spotify, etc." required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-515" />
          </div>

          <div data-id="element-516">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-517">
              Montant
            </label>
            <input type="number" value={formData.amount || ''} onChange={e => setFormData({
            ...formData,
            amount: parseFloat(e.target.value)
          })} placeholder="5000" required min="0" step="0.01" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-518" />
          </div>

          <div data-id="element-519">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-520">
              Devise
            </label>
            <select value={formData.currency} onChange={e => setFormData({
            ...formData,
            currency: e.target.value as any
          })} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-521">
              <option value="XOF" data-id="element-522">XOF (Franc CFA)</option>
              <option value="EUR" data-id="element-523">EUR (Euro)</option>
              <option value="USD" data-id="element-524">USD (Dollar)</option>
            </select>
          </div>

          <div data-id="element-525">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-526">
              Date du dernier paiement
            </label>
            <input type="date" value={formData.last_payment_date} onChange={e => setFormData({
            ...formData,
            last_payment_date: e.target.value
          })} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-527" />
            <p className="text-sm text-gray-500 mt-1" data-id="element-528">
              Le prochain paiement sera calculé 30 jours après cette date
            </p>
          </div>

          <div className="flex gap-3 pt-4" data-id="element-529">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium" data-id="element-530">
              Annuler
            </button>
            <button type="submit" disabled={isSubmitting} className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-531">
              {isSubmitting ? <>
                  <Loader2 className="w-5 h-5 animate-spin" data-id="element-532" />
                  Création...
                </> : 'Créer'}
            </button>
          </div>
        </form>
      </motion.div>
    </div>;
}